/**
 * Libellés d'écran des vocabulaires — « Un mot pour le code, un mot pour l'écran »
 * (docs/VOCABULAIRE_ECRAN.md, étape 5a). Le code garde `Thesaurus` / `ThesaurusShare` /
 * `Entree` / `cle` ; l'écran dit vocabulaire / concept / nom.
 *
 * Communs à la section Vocabulaires (`SecThesaurus`, `SharedThesaurusList`), à leurs
 * fenêtres (`ModalThesaurus`, `ShareThesaurusDialog`, `ModalSharedThesaurus`,
 * `ModalThesaurusHistory`), à l'onglet Contenu (`EntreeManager`, `ThesaurusContentView`)
 * et aux deux slices (`thesaurusSlice`, `sharedThesaurusSlice` : toasts, une phrase
 * d'erreur fixe par geste, jamais le message du serveur).
 *
 * Accords : concept et vocabulaire sont masculins ; pluriels conditionnels
 * (0 et 1 au singulier). Garde-fou : `vocabulaireLabels.test.ts` (contre `motsBannis`).
 */

import { ENTREE_WINDOW_LABELS } from '../entrees/form/entreeFormLabels';
import { MOTS_DE_L_ECRAN } from '../help/motsDeLEcran';
import { ENTREE_TABLE_LABELS } from '../entrees/entreeTableLabels';

const s = (n: number): string => (n > 1 ? 's' : '');

/** « 1 concept », « 2 concepts ». */
export const compteConcepts = (n: number): string => `${n} concept${s(n)}`;
/** « 1 vocabulaire », « 3 vocabulaires ». */
export const compteVocabulaires = (n: number): string => `${n} vocabulaire${s(n)}`;
/** « 1 participant », « 4 participants ». */
export const compteParticipants = (n: number): string => `${n} participant${s(n)}`;

/** Droit d'un participant (`ThesaurusSharePermission`) : jamais `read` / `write` à l'écran. */
const DROITS = {
  read: 'Lecture',
  write: 'Modification',
} as const;

export const libelleDroit = (permission: string): string =>
  permission === 'write' ? DROITS.write : DROITS.read;

const REESSAYER = 'Réessayez dans un instant.';
const CONNEXION = 'Vérifiez votre connexion, puis réessayez.';

/** Libellés des champs d'un concept dans l'historique d'un vocabulaire partagé. */
const CHAMPS: Readonly<Record<string, string>> = {
  cle: 'Nom',
  definition: 'Définition',
  entrees: 'Précisions',
  // Une seule source (l'aperçu d'un concept) ; `entreeTableLabels` ne peut pas lire
  // `libelleChamp` sans import circulaire, c'est donc ce module qui la reprend.
  scopeNote: ENTREE_TABLE_LABELS.apercu.noteApplication,
  historyNote: 'Note historique',
  editorialNote: 'Note éditoriale',
  notation: 'Notation',
  hiddenLabels: 'Variantes de recherche',
  labels: 'Noms par langue',
  voir: 'Voir',
  voirAussi: 'Voir aussi',
  termesGeneriques: 'Termes génériques',
  termesSpecifiques: 'Termes spécifiques',
};
const CHAMP_INCONNU = 'Autre information';

/** Libellé humain d'un champ d'historique ; un champ inconnu n'affiche jamais son nom brut. */
export const libelleChamp = (field: string): string =>
  Object.prototype.hasOwnProperty.call(CHAMPS, field) ? CHAMPS[field] : CHAMP_INCONNU;

export const VOCABULAIRE_LABELS = {
  compteConcepts,
  compteVocabulaires,
  compteParticipants,
  /** Nom de l'interrupteur et des concepts d'un vocabulaire partagé (étape 3). */
  conceptsPartages: ENTREE_TABLE_LABELS.partages,
  droits: DROITS,

  /** Les deux sortes de vocabulaires personnels (`type: 'simple' | 'composite'`). */
  types: {
    simple: 'Liste de concepts',
    composite: 'Réunion de vocabulaires',
    lie: 'Lié',
    degroupe: 'Dégroupé',
    /** Suffixe d'une option de la combinaison qui réunit déjà d'autres vocabulaires. */
    reunionLiee: '(réunion liée)',
  },

  /** Section Vocabulaires, onglet « Personnel » (`SecThesaurus`). */
  section: {
    ongletPersonnel: 'Personnel',
    ongletPartages: 'Partagés',
    rechercher: 'Rechercher un vocabulaire…',
    nouveau: 'Nouveau',
    combiner: 'Combiner',
    combinerAide: 'Réunir plusieurs vocabulaires en un seul',
    /* Écran vide commun (C24 SP2) : le titre, la phrase qui dit la chose, le geste. */
    aucun: 'Aucun vocabulaire pour l’instant',
    aucunAide: MOTS_DE_L_ECRAN.vocabulaire.ligne,
    aucunTrouve: 'Aucun vocabulaire ne correspond.',
    creerPremier: 'Créer un vocabulaire',
    sansDescription: 'Aucune description',
    voir: 'Voir le contenu',
    modifier: 'Modifier',
    partager: 'Partager',
    partagerAide: 'Partager avec des contacts',
    degrouper: 'Dégrouper',
    degrouperAide: 'Dégrouper ce vocabulaire',
    supprimer: 'Supprimer',
  },

  /** Onglet « Partagés » (`SharedThesaurusList`) et dialogue de partage (`ShareThesaurusDialog`). */
  partages: {
    rechercher: 'Rechercher un vocabulaire partagé…',
    nouveau: 'Nouveau partagé',
    aucun: 'Aucun vocabulaire partagé pour l’instant',
    aucunAide: 'Un vocabulaire partagé se lit chez vos contacts, et vous pouvez en garder des concepts.',
    creer: 'Créer un vocabulaire partagé',
    nom: 'Nom du vocabulaire',
    descriptionFacultative: 'Description (facultative)',
    creationAide: 'Vous pourrez ajouter des participants et copier des concepts après la création.',
    mesPartages: (n: number) => `Mes vocabulaires partagés (${n})`,
    partagesAvecMoi: (n: number) => `Partagés avec moi (${n})`,
    proprietaire: 'Propriétaire',
    partageAvecMoi: 'Partagé avec moi',
    proprietaireNom: (nom: string) => `Propriétaire : ${nom}`,
    droitModification: 'Modification autorisée',
    droitLecture: 'Lecture seule',
    /** Infobulle d'un participant : « Alice — Modification ». */
    participant: (nom: string, permission: string) => `${nom} — ${libelleDroit(permission)}`,
    sansDescription: 'Aucune description',
    voir: 'Voir le contenu',
    modifier: 'Modifier',
    historique: 'Historique',
    supprimer: 'Supprimer',
    reessayer: 'Réessayer',
    annuler: 'Annuler',
    creerBouton: 'Créer',

    /** Partager un vocabulaire personnel (`ShareThesaurusDialog`). */
    dialogue: {
      titre: 'Partager le vocabulaire',
      source: 'Vocabulaire source :',
      copies: (n: number) =>
        `${compteConcepts(n)} ${n > 1 ? 'seront copiés' : 'sera copié'} dans le vocabulaire partagé`,
      nomPartage: 'Nom du partage',
      nomPartageAide: 'Ce nom sera visible par les contacts avec qui vous partagez',
      descriptionFacultative: 'Description (facultative)',
      /** Valeurs pré-remplies, enregistrées telles quelles si l'on n'y touche pas. */
      nomParDefaut: (nom: string) => `${nom} (partagé)`,
      /** Espaces insécables dans les guillemets : « » ne reste jamais seul en fin de ligne. */
      descriptionParDefaut: (nom: string) => `Partage du vocabulaire «\u00a0${nom}\u00a0»`,
      droitParDefaut: 'Droit par défaut',
      lectureSeule: 'Lecture seule',
      lectureModification: 'Lecture et modification',
      partagerAvec: 'Partager avec vos contacts',
      chargementContacts: 'Chargement des contacts…',
      aucunContact:
        "Vous n'avez pas encore de contacts. Ajoutez des contacts depuis votre profil pour pouvoir partager.",
      rechercherContact: 'Rechercher un contact…',
      aucunContactDisponible: 'Aucun contact disponible',
      contactsSelectionnes: (n: number) => `Contacts sélectionnés (${n})`,
      retirerContact: (nom: string) => `Retirer ${nom}`,
      annuler: 'Annuler',
      partager: 'Partager',
      enCours: 'Partage en cours…',
      contactDejaAjoute: 'Ce contact est déjà dans la liste',
      nomRequis: 'Le nom du partage est requis',
      auMoinsUnContact: 'Ajoutez au moins un contact pour partager',
    },
  },

  /**
   * Sélecteur des vocabulaires d'une note (panneau « Détails », groupe « Sens » :
   * `NoteDetailsTab`) ; il filtre les concepts proposés par la recherche.
   */
  note: {
    champ: 'Vocabulaires',
    choisir: 'Choisir des vocabulaires…',
    aucunChoisi: 'Aucun vocabulaire choisi : tous les concepts sont proposés à la recherche.',
    choisis: (n: number) =>
      `${compteVocabulaires(n)} choisi${s(n)} : seuls ${n > 1 ? 'leurs' : 'ses'} concepts sont proposés à la recherche.`,
    /** Nom accessible du retrait d'un vocabulaire (sinon la puce dirait « Remove … »). */
    retirer: (nom: string) => `Retirer le vocabulaire ${nom}`,
  },

  /** Fenêtre d'un vocabulaire personnel (`ModalThesaurus`). */
  fenetre: {
    titreNouveau: 'Nouveau vocabulaire',
    titreModifier: (nom: string) => (nom ? `Modifier : ${nom}` : 'Modifier le vocabulaire'),
    titreDetails: (nom: string) => (nom ? `Détails : ${nom}` : 'Détails du vocabulaire'),
    titreCombiner: 'Combiner des vocabulaires',
    titreDefaut: 'Vocabulaire',
    /** Séparateur du sous-titre : « Réunion de vocabulaires • Lié • 3 concepts ». */
    separateur: ' • ',
    onglets: {
      informations: 'Informations',
      contenu: 'Contenu',
      metadonnees: 'Métadonnées',
    },
    informations: 'Informations',
    details: 'Détails du vocabulaire',
    nom: 'Nom',
    description: 'Description',
    type: 'Type',
    nomCombinaison: 'Nom du nouveau vocabulaire',
    choixSources: 'Choix des vocabulaires',
    sources: 'Vocabulaires à combiner',
    sourcesAide: 'Vous pouvez combiner des listes de concepts et des réunions liées',
    rechercher: 'Rechercher…',
    lier: 'Lier automatiquement',
    lierAide: 'Les changements des vocabulaires sources s’y reporteront automatiquement',
    copieAide: 'Le vocabulaire sera une copie figée de leurs concepts au moment de la création',
    classification: 'Classification',
    categorie: 'Catégorie',
    tags: 'Tags',
    ajouterTag: 'Ajouter un tag',
    ajouter: 'Ajouter',
    aucunTag: 'Aucun tag',
    apparence: 'Apparence',
    couleur: 'Couleur',
    fermer: 'Fermer',
    annuler: 'Annuler',
    enregistrer: 'Enregistrer',
    combiner: 'Combiner',
  },

  /** Onglet Contenu : choix des concepts (`EntreeManager`) et lecture (`ThesaurusContentView`). */
  contenu: {
    disponibles: 'Concepts disponibles',
    duVocabulaire: 'Concepts du vocabulaire',
    tous: 'Tous',
    selectionnes: 'Sélectionnés',
    /** Puce d'en-tête de colonne : le compte dans la puce, jamais en pastille par-dessus. */
    tousCompte: (n: number) => `Tous (${n})`,
    selectionnesCompte: (n: number) => `Sélectionnés (${n})`,
    rechercher: 'Rechercher…',
    effacer: 'Effacer la recherche',
    toutSelectionner: 'Tout sélectionner',
    deselectionner: 'Désélectionner',
    chargement: 'Chargement des concepts…',
    aucunCorrespondant: 'Aucun concept correspondant',
    tousDansLeVocabulaire: 'Tous vos concepts sont déjà dans ce vocabulaire',
    aucunDansLeVocabulaire: 'Aucun concept dans ce vocabulaire',
    ajouterDepuisGauche: 'Ajoutez des concepts depuis la colonne de gauche',
    ajouter: 'Ajouter',
    retirer: 'Retirer',
    ajouterSelectionnes: (n: number) => `Ajouter les sélectionnés (${n})`,
    retirerSelectionnes: (n: number) => `Retirer les sélectionnés (${n})`,
    verrouille: 'Concept venu d’un vocabulaire source : il se modifie là-bas',
    /** Bandeau d'une réunion liée (composite lié). */
    reunionLiee: {
      titre: 'Réunion liée à ses vocabulaires sources',
      proteges:
        'Les concepts liés, marqués du nom de leur source, sont protégés : ils se modifient dans leur vocabulaire d’origine.',
      propres: 'Vous pouvez ajouter des concepts propres à ce vocabulaire : ceux-là restent modifiables.',
    },

    titre: 'Contenu du vocabulaire',
    rechercherContenu: 'Rechercher dans le contenu…',
    trierPar: 'Trier par :',
    triNom: 'Nom',
    triSource: 'Source',
    triSourceNom: 'Source + nom',
    reinitialiser: 'Réinitialiser',
    options: "Options d'affichage :",
    numeros: 'Numéros',
    numerosMasquer: 'Masquer la numérotation',
    numerosAfficher: 'Afficher la numérotation',
    definitions: 'Définitions',
    definitionsMasquer: 'Masquer les définitions',
    definitionsAfficher: 'Afficher les définitions',
    precisionsApercu: 'Précisions (aperçu)',
    precisionsApercuMasquer: "Masquer les noms des précisions dans l'aperçu",
    precisionsApercuAfficher: "Afficher les noms des précisions dans l'aperçu",
    apercu: 'Aperçu (replié)',
    apercuMasquer: "Masquer l'aperçu",
    apercuAfficher: "Afficher l'aperçu",
    sources: 'Sources',
    sourcesMasquer: 'Masquer les sources',
    sourcesAfficher: 'Afficher les sources',
    sourcesListe: 'Sources :',
    toutReplier: 'Tout replier',
    toutDeplier: 'Tout déplier',
    deplier: 'Déplier',
    replier: 'Replier',
    dePlus: (n: number) => `+${n} de plus…`,
    precisions: (n: number) => `Précisions (${n})`,
    vocabulairesSources: (n: number) => (n > 1 ? 'Vocabulaires sources :' : 'Vocabulaire source :'),
  },

  /** Fenêtre d'un vocabulaire partagé (`ModalSharedThesaurus`). */
  partage: {
    titreDefaut: 'Vocabulaire partagé',
    titreDetails: (nom: string) => `Détails : ${nom}`,
    titreModifier: (nom: string) => `Modifier : ${nom}`,
    sousTitre: 'Vocabulaire partagé',
    proprietaire: 'Propriétaire',
    par: (nom: string) => `Par ${nom}`,
    droitModification: 'Modification autorisée',
    droitLecture: 'Lecture seule',
    fermer: 'Fermer',
    annuler: 'Annuler',
    enregistrer: 'Enregistrer',
    onglets: {
      informations: 'Informations',
      contenu: 'Contenu',
      participants: 'Participants',
    },
    nom: 'Nom',
    description: 'Description',
    infosPartage: 'Informations de partage',
    vousProprietaire: 'Vous êtes le propriétaire',
    partagePar: (nom: string) => `Partagé par ${nom}`,
    creeLe: (date: string) => `Créé le : ${date}`,
    modifieLe: (date: string) => `Dernière modification : ${date}`,
    vide: 'Ce vocabulaire ne contient aucun concept',

    /** Rubrique de l'onglet Informations : copier le vocabulaire entier chez soi. */
    copieVocabulaire: {
      titre: 'Copier vers mes vocabulaires',
      aide:
        'Créez une copie de ce vocabulaire partagé parmi vos vocabulaires : ses concepts sont copiés dans vos concepts, et un nouveau vocabulaire à vous les réunit.',
      bouton: (n: number) => `Copier le vocabulaire (${compteConcepts(n)})`,
      enCours: (fait: number, total: number) => `Copie en cours… ${fait}/${total}`,
      vide: 'Ce vocabulaire ne contient aucun concept à copier.',
      /** Valeurs enregistrées sur le vocabulaire créé par la copie. */
      nomCopie: (nom: string) => `${nom} (copie)`,
      descriptionCopie: '[Copié depuis le vocabulaire partagé]',
    },

    /** Rubrique de l'onglet Contenu (lecture) : copier des concepts chez soi. */
    copieConcepts: {
      titre: 'Copier vers mes concepts',
      aide: 'Cochez les concepts à copier parmi vos concepts',
      toutSelectionner: 'Tout sélectionner',
      toutDeselectionner: 'Tout désélectionner',
      copier: (n: number) => `Copier (${n})`,
      enCours: 'Copie en cours…',
      infobulle: 'Copier vers mes concepts',
    },

    /** Onglet Contenu (modification) : deux colonnes. */
    edition: {
      disponibles: 'Concepts disponibles',
      duVocabulaire: 'Concepts du vocabulaire',
      tous: 'Tous',
      selectionnes: 'Sélectionnés',
      tousCompte: (n: number) => `Tous (${n})`,
      selectionnesCompte: (n: number) => `Sélectionnés (${n})`,
      rechercher: 'Rechercher…',
      effacer: 'Effacer la recherche',
      toutSelectionner: 'Tout sélectionner',
      aucunTrouve: 'Aucun concept trouvé',
      tousDeja: 'Tous vos concepts sont déjà dans ce vocabulaire',
      aucun: 'Aucun concept dans ce vocabulaire',
      ajouterDepuisGauche: 'Ajoutez des concepts depuis la colonne de gauche',
      nouveauConcept: ENTREE_WINDOW_LABELS.titreNouveauPartage,
      modifierConcept: ENTREE_WINDOW_LABELS.titreModifier,
      ajouter: 'Ajouter',
      retirer: 'Retirer',
      ajouterSelectionnes: (n: number) => `Ajouter les sélectionnés (${n})`,
      retirerSelectionnes: (n: number) => `Retirer les sélectionnés (${n})`,
    },

    /** Onglet Participants. */
    participants: {
      ajouter: 'Ajouter un participant',
      contact: 'Choisir un contact',
      rechercher: 'Rechercher…',
      droit: 'Droit',
      lectureSeule: 'Lecture seule',
      lectureModification: 'Lecture et modification',
      ajouterBouton: 'Ajouter',
      annuler: 'Annuler',
      tousDeja: 'Tous vos contacts participent déjà à ce vocabulaire.',
      tousDejaCourt: 'Tous vos contacts participent déjà.',
      liste: (n: number) => `Liste des participants (${n})`,
      vous: 'Vous',
      proprietaire: 'Propriétaire',
      tousLesDroits: 'Tous les droits',
      /** Nom accessible du choix du droit d'un participant. */
      droitDe: (nom: string) => `Droit de ${nom}`,
      retirer: 'Retirer du vocabulaire',
      aucun: 'Aucun participant (seulement le propriétaire)',
    },
  },

  /** Historique d'un vocabulaire partagé (`ModalThesaurusHistory`). */
  historique: {
    titre: (nom: string) => `Historique — ${nom}`,
    compte: (n: number) => `${n} modification${s(n)} enregistrée${s(n)}`,
    /** Puces d'action : le sujet est un concept, donc au masculin. */
    actions: {
      created: 'Créé',
      copied_from: 'Copié depuis',
      updated: 'Modifié',
      deleted: 'Supprimé',
      copied_to: 'Copié vers',
    },
    filtre: "Type d'action",
    filtres: {
      tous: 'Toutes',
      created: 'Créations',
      copied_from: 'Copies depuis',
      updated: 'Modifications',
      deleted: 'Suppressions',
      copied_to: 'Copies vers',
    },
    champs: CHAMPS,
    champInconnu: CHAMP_INCONNU,
    /** Champ inconnu dont les deux valeurs sont illisibles : une ligne sans flèche. */
    champInconnuModifie: `${CHAMP_INCONNU} modifiée`,
    /** Valeur d'un objet inconnu : jamais de JSON à l'écran. */
    valeurModifiee: 'modifié',
    valeurVide: 'vide',
    /** Valeur booléenne d'un champ. */
    oui: 'oui',
    non: 'non',
    /** Séparateur d'une valeur en liste (variantes, renvois). */
    separateurListe: ', ',
    exporter: 'Exporter…',
    aucun: 'Aucun historique',
    aucunType: "Aucune action de ce type n'a été enregistrée",
    vide: 'Les modifications apportées à ce vocabulaire apparaîtront ici',
    fermer: 'Fermer',
  },

  /** Confirmations (`window.confirm`). */
  confirmations: {
    supprimer: (nom: string) => `Supprimer le vocabulaire « ${nom} » ? Cette action est irréversible.`,
    degrouper: (nom: string) =>
      `Dégrouper « ${nom} » ?\n\nCe vocabulaire devient une simple liste de ses concepts ; ` +
      'les changements de ses vocabulaires sources ne s’y reporteront plus.',
    supprimerPartage: (nom: string) =>
      `Supprimer le vocabulaire partagé « ${nom} » ? Il disparaîtra pour tous ses participants.`,
    retirerParticipant: (nom: string) => `Retirer ${nom} de ce vocabulaire partagé ?`,
    copierVocabulaire: (nom: string, n: number) =>
      `Copier le vocabulaire « ${nom} » et ${n > 1 ? `ses ${n} concepts` : 'son concept'} parmi les vôtres ?\n\n` +
      'Un nouveau vocabulaire sera créé et ses concepts seront copiés dans vos concepts.',
  },

  /** Notifications de succès ou d'avertissement (barre d'état). */
  toasts: {
    partageCree: 'Vocabulaire partagé créé',
    partageModifie: 'Vocabulaire partagé mis à jour',
    partageSupprime: 'Vocabulaire partagé supprimé',
    participantAjoute: (nom: string) => `${nom} ajouté au vocabulaire`,
    participantRetire: (nom: string) => `${nom} retiré du vocabulaire`,
    droitModifie: 'Droit mis à jour',
    conceptCree: 'Concept partagé créé',
    conceptModifie: 'Concept partagé mis à jour',
    conceptSupprime: 'Concept partagé supprimé',
    conceptCopieVersPartage: 'Concept copié vers le vocabulaire partagé',
    conceptCopieVersMoi: 'Concept copié vers vos concepts',
    conceptExisteDeja: (nom: string) => `Un concept nommé « ${nom} » existe déjà parmi vos concepts`,
    conceptExisteDejaSansNom: 'Un concept du même nom existe déjà parmi vos concepts',
    existeDejaDansPartage: (nom: string) => `Un concept nommé « ${nom} » existe déjà dans ce vocabulaire partagé`,
    ignores: (n: number) =>
      `${compteConcepts(n)} ignoré${s(n)} : un concept du même nom existe déjà dans le vocabulaire`,
    copies: (n: number) => `${compteConcepts(n)} copié${s(n)}`,
    copiesAvecDoublons: (fait: number, doublons: number) =>
      `${fait} copié${s(fait)}, ${doublons} déjà présent${s(doublons)} (même nom)`,
    vocabulaireCopie: (nom: string, n: number) => `Vocabulaire « ${nom} » copié avec ${compteConcepts(n)}`,
    vocabulaireCopieAvecDoublons: (fait: number, doublons: number) =>
      `Vocabulaire créé : ${fait} copié${s(fait)}, ${doublons} déjà présent${s(doublons)} (même nom)`,
    reglagesModifies: 'Paramètres mis à jour',
  },

  /**
   * Une phrase fixe par geste (`rejectWithValue`), jamais le message du serveur ni
   * d'axios ; le détail part dans la console.
   */
  erreurs: {
    /** `thesaurusSlice`. */
    personnels: {
      chargement: `Vos vocabulaires n'ont pas pu être chargés. ${REESSAYER}`,
      chargementUn: `Ce vocabulaire n'a pas pu être chargé. ${REESSAYER}`,
      recherche: `La recherche n'a pas abouti. ${REESSAYER}`,
      creation: `Le vocabulaire n'a pas pu être créé. ${CONNEXION}`,
      modification: `Le vocabulaire n'a pas pu être enregistré. ${CONNEXION}`,
      suppression: `Le vocabulaire n'a pas pu être supprimé. ${REESSAYER}`,
      ajoutConcepts: `Les concepts n'ont pas pu être ajoutés au vocabulaire. ${REESSAYER}`,
      retraitConcepts: `Les concepts n'ont pas pu être retirés du vocabulaire. ${REESSAYER}`,
      combinaison: `Les vocabulaires n'ont pas pu être combinés. ${REESSAYER}`,
      degroupage: `Le vocabulaire n'a pas pu être dégroupé. ${REESSAYER}`,
      enregistrement: `Le vocabulaire n'a pas pu être enregistré. ${CONNEXION}`,
    },
    /** `sharedThesaurusSlice` et `ModalSharedThesaurus`. */
    partages: {
      chargement: `Les vocabulaires partagés n'ont pas pu être chargés. ${REESSAYER}`,
      chargementMentions: `Les concepts partagés n'ont pas pu être chargés. ${REESSAYER}`,
      chargementUn: `Ce vocabulaire partagé n'a pas pu être ouvert. ${REESSAYER}`,
      creation: `Le vocabulaire partagé n'a pas pu être créé. ${CONNEXION}`,
      partage: `Le vocabulaire n'a pas pu être partagé. ${CONNEXION}`,
      modification: `Le vocabulaire partagé n'a pas pu être enregistré. ${CONNEXION}`,
      suppression: `Le vocabulaire partagé n'a pas pu être supprimé. ${REESSAYER}`,
      ajoutParticipant: `Le participant n'a pas pu être ajouté. ${REESSAYER}`,
      retraitParticipant: `Le participant n'a pas pu être retiré. ${REESSAYER}`,
      droitParticipant: `Le droit du participant n'a pas pu être changé. ${REESSAYER}`,
      chargementConcepts: `Les concepts de ce vocabulaire partagé n'ont pas pu être chargés. ${REESSAYER}`,
      creationConcept: `Le concept partagé n'a pas pu être créé. ${REESSAYER}`,
      modificationConcept: `Le concept partagé n'a pas pu être enregistré. ${REESSAYER}`,
      suppressionConcept: `Le concept n'a pas pu être retiré du vocabulaire partagé. ${REESSAYER}`,
      ajoutConcept: `Le concept n'a pas pu être copié dans le vocabulaire partagé. ${REESSAYER}`,
      ajoutConceptPlusieurs: (n: number) =>
        `${compteConcepts(n)} n'${n > 1 ? 'ont' : 'a'} pas pu être copié${s(n)} dans le vocabulaire partagé. ${REESSAYER}`,
      suppressionConceptPlusieurs: (n: number) =>
        `${compteConcepts(n)} n'${n > 1 ? 'ont' : 'a'} pas pu être retiré${s(n)} du vocabulaire partagé. ${REESSAYER}`,
      copieVersMoi: `Le concept n'a pas pu être copié parmi vos concepts. ${REESSAYER}`,
      copieVersMoiPlusieurs: (n: number) =>
        `${compteConcepts(n)} n'${n > 1 ? 'ont' : 'a'} pas pu être copié${s(n)}. ${REESSAYER}`,
      copieVocabulaire: `Le vocabulaire n'a pas pu être copié. ${REESSAYER}`,
      /** Aucun concept n'a pu être copié (un doublon, lui, rejoint le vocabulaire copié). */
      copieVocabulaireVide: `Le vocabulaire n'a pas pu être copié : aucun de ses concepts n'a pu l'être. ${REESSAYER}`,
      historique: `L'historique n'a pas pu être chargé. ${REESSAYER}`,
      /** Refus de droit (403) d'un geste qui modifie le vocabulaire partagé ou ses concepts. */
      refusModification: "Vous n'avez pas le droit de modifier ce vocabulaire partagé.",
      /** Refus de droit (403) d'un geste sur les participants ou leurs droits. */
      refusParticipants: 'Seule la personne qui partage ce vocabulaire peut gérer ses participants.',
      reglages: `Les paramètres n'ont pas pu être enregistrés. ${REESSAYER}`,
    },
  },
} as const;
